import { defineStore } from 'pinia'
import { useShipStore } from '@/stores/ship'
import API from '@/api/apiUrls'
import { useAuthenticationStore } from '@/stores/authentication'
import type { ShipType } from '@/stores/type/ship.type'
import { useApi } from '@/api/useApi'
import { useEventsBus } from '@haskoning/vue-component-library'
import { useBaseStore } from '@haskoning/vue-basestore'
import { ref } from 'vue'
import type { DocumentType, UploadDocumentType } from '@/stores/type/document.type'

const api = useApi<DocumentType>()

const baseUrl = API.DOCUMENT.BASE

export const useDocumentStore = defineStore('document', () => {
	const baseStore = useBaseStore<DocumentType>({ service: baseUrl, api })

	const documentItems = ref<UploadDocumentType[]>([])
	const uploadLoading = ref(false)
	const downloadLoading = ref(false)
	const uploadDocumentFiles = async (shipId: number): Promise<void> => {
		uploadLoading.value = true
		const unUploadedItems = documentItems.value.filter((item) => !item.sent)
		for (const item of unUploadedItems) {
			if (item.file) {
				await uploadDocumentFile(item.file, shipId)
			}
		}
		uploadLoading.value = false
	}
	const setDocuments = (documents: DocumentType[]): void => {
		documentItems.value = documents.map((document) => ({
			...document,
			failed: false,
			loading: false,
			sent: true
		}))
	}
	const resetDocuments = (): void => {
		documentItems.value = []
	}
	const clearUnsavedDocuments = (): void => {
		documentItems.value = documentItems.value.filter((item) => item.sent && !item.failed)
	}
	const addFiles = (files: File[]): void => {
		const uploadFiles: UploadDocumentType[] = files.map((file) => {
			return { file, failed: false, loading: false, sent: false, file_name: file.name }
		})
		documentItems.value.unshift(...uploadFiles)
	}
	const removeUnsentFiles = (): void => {
		documentItems.value = documentItems.value.filter((item) => item.sent)
	}
	const uploadDocumentFile = async (file: File, shipId: number): Promise<boolean> => {
		const index = documentItems.value.findIndex((item) => item.file === file)
		if (index !== -1) {
			const item = documentItems.value[index]
			try {
				item.loading = true
				const url = API.DOCUMENT.UPLOAD
				const formData = new FormData()
				formData.append('ship', shipId.toString())
				formData.append('file', item.file as Blob)
				const response = (await api.service(url).post(formData)) as DocumentType | null
				if (response) {
					item.sent = true
					item.response = response
					item.id = response.id
					item.file_name = response.file_name
				}

				const { emit } = useEventsBus()
				const uploadMessage = {
					status: 200,
					data: [],
					method: 'POST',
					statusText: 'Uploaded document!'
				}
				emit('success', uploadMessage)

				item.loading = false
				return true
			} catch {
				item.failed = true
				item.loading = false
				item.sent = true
				return false
			}
		}
		return false
	}
	const downloadDocument = async (id: number): Promise<void> => {
		downloadLoading.value = true
		const url = API.DOCUMENT.DOWNLOAD(id)
		const shipStore = useShipStore()
		await shipStore.download(url)
		downloadLoading.value = false
	}
	const documentPermissions = (ship: ShipType | null): boolean => {
		if (!ship) return false
		const djangoAuthStore = useAuthenticationStore()
		const isNotApproved = ship.validation_set?.every((approval) => !approval.approved) ?? false
		return (
			djangoAuthStore.user?.is_superuser ||
			(ship.user === djangoAuthStore.user?.id && isNotApproved)
		)
	}

	return {
		...baseStore,
		uploadDocumentFiles,
		setDocuments,
		resetDocuments,
		clearUnsavedDocuments,
		addFiles,
		removeUnsentFiles,
		uploadDocumentFile,
		downloadDocument,
		documentPermissions,
		documentItems,
		uploadLoading,
		downloadLoading
	}
})
